import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMediumArticles, cleanImagePaths, resolveImagePath, resolveHtmlImagePaths } from '../../hooks/useMediumArticles';
import { useTranslation } from 'react-i18next';
import SEOHead from '../SEOHead';
import DesktopNavigation from '../Navigation/DesktopNavigation';
import MobileNavigation from '../Navigation/MobileNavigation';
import Footer from '../Footer/Footer';
import { FaMedium, FaArrowLeft } from 'react-icons/fa';
import { CATEGORIES, inferCategories } from '../../constants/articleCategories';

export const ArticleDetailPage: React.FC = () => {
  const { articleId } = useParams<{ articleId: string }>();
  const { t, i18n } = useTranslation();
  const isEs = i18n.language.startsWith('es');

  const { articles, loading } = useMediumArticles();
  
  const [fetchedContent, setFetchedContent] = React.useState<string | null>(null);
  const [fetchLoading, setFetchLoading] = React.useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Find the article by ID
  const article = articles.find((a) => {
    const cleanParamId = articleId?.toLowerCase() || '';
    return (
      a.id.toLowerCase() === cleanParamId ||
      a.url.toLowerCase().includes(cleanParamId) ||
      a.id.toLowerCase().includes(cleanParamId)
    );
  });

  useEffect(() => {
    if (!article || article.content) {
      setFetchedContent(null);
      setFetchLoading(false);
      return;
    }

    let isCurrent = true;
    const loadContent = async () => {
      setFetchLoading(true);
      try {
        let htmlText = '';

        const apiBase =
          process.env.REACT_APP_API_URL ||
          process.env.NEXT_PUBLIC_API_URL ||
          (process.env.NODE_ENV === 'production'
            ? 'https://christreignsmedia-api-b91e81f2bbea.herokuapp.com'
            : 'http://localhost:3001');

        // Try Proxy 0: NestJS backend scraper (uses Puppeteer to bypass Cloudflare)
        try {
          const backendUrl = `${apiBase}/articles/content?url=${encodeURIComponent(article.url)}`;
          const response = await fetch(backendUrl);
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.content) {
              htmlText = data.content;
            }
          }
        } catch (e) {
          console.warn('Backend proxy failed, trying public proxies...', e);
        }

        // Try Proxy 1: corsproxy.io
        if (!htmlText) {
          try {
            const corsProxyUrl = `https://corsproxy.io/?${encodeURIComponent(article.url)}`;
            const response = await fetch(corsProxyUrl);
            if (response.ok) {
              htmlText = await response.text();
              if (htmlText.includes('Server-side requests are not allowed')) {
                htmlText = '';
              }
            }
          } catch (e) {
            console.warn('Proxy 1 failed, trying proxy 2...', e);
          }
        }

        // Try Proxy 2: AllOrigins
        if (!htmlText) {
          try {
            const allOriginsUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(article.url)}`;
            const response = await fetch(allOriginsUrl);
            if (response.ok) {
              const json = await response.json();
              htmlText = json.contents || '';
            }
          } catch (e) {
            console.warn('Proxy 2 failed, trying proxy 3...', e);
          }
        }

        // Try Proxy 3: cors.lol
        if (!htmlText) {
          try {
            const corsLolUrl = `https://api.cors.lol/?url=${encodeURIComponent(article.url)}`;
            const response = await fetch(corsLolUrl);
            if (response.ok) {
              htmlText = await response.text();
            }
          } catch (e) {
            console.warn('Proxy 3 failed', e);
          }
        }

        if (htmlText && isCurrent) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlText, 'text/html');
          const articleEl = doc.querySelector('article');
          
          if (articleEl) {
            // Remove first h1/h2/h3 if it matches the title to avoid double headers
            const headerEl = articleEl.querySelector('h1, h2, h3');
            if (headerEl && (headerEl.textContent?.trim().toLowerCase() === article.title.toLowerCase() || headerEl.textContent?.trim().length === 0)) {
              headerEl.remove();
            }
            
            // Remove all Speechify ignore containers (author metadata, claps, bookmarks, audio/share controls)
            const speechifyIgnore = articleEl.querySelectorAll('.speechify-ignore');
            speechifyIgnore.forEach(el => el.remove());

            // Remove elements by specific testids just in case they are outside speechify-ignore wrappers
            const testIdsToRemove = [
              'authorPhoto',
              'authorName',
              'storyReadTime',
              'headerClapButton',
              'headerBookmarkButton',
              'audioPlayButton',
              'headerSocialShareButton'
            ];
            testIdsToRemove.forEach(id => {
              articleEl.querySelectorAll(`[data-testid="${id}"]`).forEach(el => {
                const wrapper = el.closest('.speechify-ignore') || el.closest('div');
                if (wrapper && wrapper !== articleEl) {
                  wrapper.remove();
                } else {
                  el.remove();
                }
              });
            });

            // Remove any leftover author / follow button wrappers
            const links = articleEl.querySelectorAll('a, button, span');
            links.forEach(el => {
              if (el.textContent?.trim() === 'Follow') {
                el.parentElement?.remove();
              }
            });

            setFetchedContent(articleEl.innerHTML);
          } else {
            // Fallback: try taking elements with class matching common medium classes
            const postContent = doc.querySelector('.postArticle-content') || doc.querySelector('.section-content');
            if (postContent) {
              setFetchedContent(postContent.innerHTML);
            }
          }
        }
      } catch (err) {
        console.error('Failed to dynamically fetch article content:', err);
      } finally {
        if (isCurrent) {
          setFetchLoading(false);
        }
      }
    };

    loadContent();

    return () => {
      isCurrent = false;
    };
  }, [article]);

  useEffect(() => {
    if (!contentRef.current || fetchLoading) return;

    // Select all images in the HTML container
    const images = contentRef.current.getElementsByTagName('img');
    const handleImageError = (e: Event) => {
      const img = e.target as HTMLImageElement;
      const currentSrc = img.src;
      
      // Prevent infinite loops if fallback also fails
      if (img.getAttribute('data-tried-fallback') === 'true') {
        img.style.display = 'none';
        const parentFigure = img.closest('figure');
        if (parentFigure) {
          parentFigure.style.display = 'none';
        }
        return;
      }

      // Strip WordPress resized suffix: e.g. -768x766.avif -> .avif
      const cleanSrc = currentSrc.replace(/-\d+x\d+(\.[a-zA-Z0-9]+)$/i, '$1');
      
      if (currentSrc !== cleanSrc) {
        img.setAttribute('data-tried-fallback', 'true');
        img.src = cleanSrc;
        if (img.srcset) {
          img.removeAttribute('srcset');
        }
      } else {
        img.style.display = 'none';
        const parentFigure = img.closest('figure');
        if (parentFigure) {
          parentFigure.style.display = 'none';
        }
      }
    };

    // Attach listeners
    for (let i = 0; i < images.length; i++) {
      images[i].addEventListener('error', handleImageError);
    }

    // Cleanup listeners
    return () => {
      for (let i = 0; i < images.length; i++) {
        images[i].removeEventListener('error', handleImageError);
      }
    };
  }, [fetchedContent, article?.content, fetchLoading]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return new Intl.DateTimeFormat(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(date);
    } catch {
      return dateStr;
    }
  };

  if (loading && !article) {
    return (
      <div className="min-h-screen bg-white">
        <DesktopNavigation />
        <MobileNavigation />
        <div className="flex flex-col items-center justify-center py-48">
          <style dangerouslySetInnerHTML={{ __html: `
            @keyframes spin-slow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            .custom-spinner { animation: spin-slow 1s linear infinite; }
          ` }} />
          <div className="relative w-16 h-16">
            <div className="custom-spinner absolute inset-0 rounded-full border-4 border-transparent border-t-[#05a5b7]" />
          </div>
          <span className="mt-6 text-gray-500 text-xs font-bold tracking-widest uppercase animate-pulse">
            {t('articles.loading')}
          </span>
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <DesktopNavigation />
        <MobileNavigation />
        <div className="py-24 max-w-2xl mx-auto px-4 text-center">
          <div className="bg-gray-50 border border-gray-100 rounded-3xl p-12 shadow-sm">
            <span className="text-6xl block mb-6" role="img" aria-label="warning">🔍</span>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              {isEs ? 'Artículo no encontrado' : 'Article Not Found'}
            </h3>
            <p className="text-gray-500 mb-8">
              {isEs 
                ? 'El artículo que buscas no existe o ha sido movido.'
                : 'The article you are looking for does not exist or has been moved.'}
            </p>
            <Link
              to="/articles"
              className="px-8 py-3 bg-[#05a5b7] text-white rounded-full text-sm font-bold hover:bg-[#048a99] transition-all shadow"
            >
              {isEs ? 'Volver a Artículos' : 'Back to Articles'}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const cats = inferCategories(article.title, article.subtitle || '');
  const primaryCat = CATEGORIES.find((c) => c.id === cats[0]);

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans overflow-x-hidden selection:bg-[#05a5b7] selection:text-white">
      <SEOHead 
        title={article.title}
        description={article.subtitle}
      />
      
      <DesktopNavigation />
      <MobileNavigation />
      
      <main className="pt-28 xl:pt-36 pb-24">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          {/* Back Button */}
          <Link
            to="/articles"
            className="inline-flex items-center text-sm font-semibold text-gray-500 hover:text-[#05a5b7] transition-colors mb-8 group"
          >
            <FaArrowLeft className="w-4 h-4 mr-2 transform transition-transform group-hover:-translate-x-1" />
            {isEs ? 'Volver a Artículos' : 'Back to Articles'}
          </Link>

          {/* Article Header */}
          <header className="mb-10">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              {primaryCat && (
                <span
                  className="px-3 py-1 text-xs font-bold uppercase tracking-wider text-white rounded-full"
                  style={{ backgroundColor: primaryCat.color }}
                >
                  {isEs ? primaryCat.labelEs : primaryCat.labelEn}
                </span>
              )}
              <span className="px-2.5 py-1 text-[10px] font-bold tracking-widest text-gray-500 uppercase bg-gray-100 rounded-md">
                {article.language.toUpperCase()}
              </span>
              {article.publishedAt && (
                <span className="text-sm text-gray-500 font-medium">
                  {formatDate(article.publishedAt)}
                </span>
              )}
            </div>

            <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 leading-tight tracking-tight">
              {article.title}
            </h1>


            {/* Read on Medium Button */}
            {article.url && article.url.includes('medium.com') && (
              <div className="flex items-center gap-4">
                <a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white hover:bg-gray-800 transition-colors rounded-full text-xs font-bold uppercase tracking-wider shadow-sm"
                >
                  <FaMedium className="w-4 h-4" />
                  {isEs ? 'Leer en Medium' : 'Read on Medium'}
                </a>
              </div>
            )}
          </header>

          {/* Cover Image:
              - Cloudinary (new articles): always show — content doesn't embed the image
              - Legacy wp-content / miro: only show when there's no fetched content (content already contains images inline) */}
          {article.imageUrl && (
            article.imageUrl.includes('cloudinary.com') ||
            (!article.content && !fetchedContent)
          ) && (
            <div className="relative w-full rounded-3xl overflow-hidden shadow-md mb-12 aspect-[21/9] bg-gray-50">
              <img
                src={resolveImagePath(article.imageUrl)}
                alt={article.title}
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  const original = img.getAttribute('data-original-src');
                  if (original && img.src !== original) {
                    img.src = original;
                  } else {
                    img.parentElement?.remove();
                  }
                }}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Full content / fallbacks */}
          <article className="medium-content">
            {article.content || fetchedContent ? (
              <div 
                ref={contentRef}
                className="medium-html-content"
                dangerouslySetInnerHTML={{ __html: resolveHtmlImagePaths(cleanImagePaths(article.content || fetchedContent || '')) || '' }} 
              />
            ) : fetchLoading ? (
              <div className="flex flex-col items-center justify-center py-16 bg-gray-50 border border-gray-100 rounded-3xl max-w-xl mx-auto mt-8">
                <style dangerouslySetInnerHTML={{ __html: `
                  @keyframes spin-slow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                  .custom-spinner { animation: spin-slow 1s linear infinite; }
                ` }} />
                <div className="relative w-12 h-12 mb-4">
                  <div className="custom-spinner absolute inset-0 rounded-full border-4 border-transparent border-t-[#05a5b7]" />
                </div>
                <span className="text-gray-500 text-xs font-bold tracking-widest uppercase animate-pulse">
                  {isEs ? 'Recuperando contenido...' : 'Retrieving content...'}
                </span>
              </div>
            ) : (
              <div className="text-center py-16 bg-gray-50 border border-gray-100 rounded-3xl px-6 max-w-xl mx-auto mt-8">
                <span className="text-5xl block mb-4">📰</span>
                <h4 className="text-lg font-bold text-gray-800 mb-2">
                  {isEs ? 'Contenido completo en Medium' : 'Full Content Available on Medium'}
                </h4>
                <p className="text-sm text-gray-500 mb-6">
                  {isEs
                    ? 'Este artículo está archivado. Puedes leer la publicación completa con todas sus imágenes directamente en la plataforma Medium.'
                    : 'This article is archived. You can read the full, beautifully formatted publication with all its graphics directly on Medium.'}
                </p>
                <a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#05a5b7] text-white hover:bg-[#048a99] transition-colors rounded-full text-sm font-bold shadow-md"
                >
                  <FaMedium className="w-5 h-5" />
                  {isEs ? 'Leer Artículo Completo' : 'Read Full Article'}
                </a>
              </div>
            )}
          </article>
        </div>
      </main>

      <Footer />

      <style dangerouslySetInnerHTML={{ __html: `
        .medium-html-content {
          color: #2d3748;
          line-height: 1.8;
          font-size: 1.125rem;
          font-family: Georgia, Cambria, "Times New Roman", Times, serif;
        }
        .medium-html-content p {
          margin-bottom: 1.5rem;
          font-family: Georgia, Cambria, "Times New Roman", Times, serif;
        }
        .medium-html-content h2, .medium-html-content h3, .medium-html-content h4 {
          color: #1a202c;
          font-weight: 700;
          margin-top: 2.5rem;
          margin-bottom: 1.25rem;
          line-height: 1.3;
          font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", "Segoe UI", Arial, sans-serif;
          letter-spacing: -0.016em;
        }
        .medium-html-content h2 { font-size: 1.75rem; }
        .medium-html-content h3 { font-size: 1.5rem; }
        .medium-html-content h4 { font-size: 1.25rem; }
        .medium-html-content figure {
          margin: 2.5rem 0;
          text-align: center;
        }
        .medium-html-content img {
          max-width: 100%;
          height: auto;
          border-radius: 1.5rem;
          margin: 0 auto;
          box-shadow: 0 4px 20px rgba(0,0,0,0.05);
        }
        .medium-html-content figcaption {
          font-size: 0.875rem;
          color: #718096;
          margin-top: 0.75rem;
          font-style: italic;
        }
        .medium-html-content blockquote {
          border-left: 4px solid #05a5b7;
          padding-left: 1.5rem;
          font-style: italic;
          color: #4a5568;
          margin: 2rem 0;
          font-size: 1.25rem;
          font-family: Georgia, Cambria, "Times New Roman", Times, serif;
        }
        .medium-html-content ul, .medium-html-content ol {
          margin-left: 2rem;
          margin-bottom: 1.75rem;
          font-family: Georgia, Cambria, "Times New Roman", Times, serif;
        }
        .medium-html-content ul { list-style-type: disc; }
        .medium-html-content ol { list-style-type: decimal; }
        .medium-html-content li {
          margin-bottom: 0.5rem;
        }
        .medium-html-content a {
          color: #05a5b7;
          text-decoration: underline;
          font-weight: 600;
        }
        .medium-html-content a:hover {
          color: #048a99;
        }
        .medium-html-content pre, .medium-html-content code {
          background-color: #f7fafc;
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-family: Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.9em;
          color: #e53e3e;
        }
        .medium-html-content pre {
          padding: 1.25rem;
          overflow-x: auto;
          margin-bottom: 1.75rem;
          border-radius: 1rem;
          border: 1px solid #edf2f7;
          color: #2d3748;
        }
        .medium-html-content pre code {
          background-color: transparent;
          padding: 0;
          font-size: 0.875rem;
          color: inherit;
        }
        .medium-html-content strong, .medium-html-content b {
          font-weight: 700;
        }
        .medium-html-content em, .medium-html-content i {
          font-style: italic;
        }
      ` }} />
    </div>
  );
};

export default ArticleDetailPage;
